// Sidebar — brand block, primary CTA, nav items, footer account chip.
// Per spec §10.1.

import {
  Home,
  Zap,
  LayoutGrid,
  Package,
  Palette,
  Users,
  HelpCircle,
  Plus,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { CustomerSummary } from '../../lib/types';
import type { ViewName } from '../../lib/store';
import { listDesigns } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { AccountMenu } from './AccountMenu';

interface NavItem {
  view: ViewName;
  label: string;
  icon: typeof Home;
  group: 'primary' | 'business' | 'footer';
  badge?: string | number;
}

interface SidebarProps {
  customer: CustomerSummary;
  view: ViewName;
  onNavigate: (next: ViewName) => void;
}

export function Sidebar({ customer, view, onNavigate }: SidebarProps) {
  // Fetch a count for the My Designs sidebar badge — matches the mockup
  // pattern where active counts read like "47" / "23" next to nav items.
  // Light query (limit:1, only the .total field is used), 60s staleTime
  // so navigation between views doesn't refetch.
  const designsCountQ = useQuery({
    queryKey: qk.designs({ sidebar: true }),
    queryFn: () => listDesigns({ limit: 1 }),
    staleTime: 60_000,
  });
  const designsCount = designsCountQ.data?.total;

  // Icons per spec §5 Icons table. Brand Kit is `palette` (the spec
  // table is canonical; the mockup HTML showed a gift box, but Kimi
  // confirmed spec wins for canonical iconography).
  const NAV: NavItem[] = [
    { view: 'home',    label: 'Home',          icon: Home,        group: 'primary' },
    { view: 'reorder', label: 'Quick Reorder', icon: Zap,         group: 'primary' },
    {
      view: 'designs',
      label: 'My Designs',
      icon: LayoutGrid,
      group: 'primary',
      badge: typeof designsCount === 'number' && designsCount > 0 ? designsCount : undefined,
    },
    { view: 'orders',  label: 'Orders',        icon: Package,     group: 'primary' },
    { view: 'kit',     label: 'Brand Kit',     icon: Palette,     group: 'business' },
    { view: 'team',    label: 'My Team',       icon: Users,       group: 'business' },
    { view: 'help',    label: 'Help & Tutorials', icon: HelpCircle, group: 'footer' },
  ];

  const primary = NAV.filter((n) => n.group === 'primary');
  const business = NAV.filter((n) => n.group === 'business');
  const footer = NAV.filter((n) => n.group === 'footer');

  // Sidebar logo doubles as the "exit Studio" affordance — clicking the
  // wordmark goes back to the brokerage portal home. This is the universal
  // web-app pattern (Canva, Figma, Notion all do it). The takeover model
  // hides the storefront's nav, so without this the only way out is the
  // account dropdown — too buried for a primary escape hatch.
  const portalHome = `https://${customer.shopify_shop_domain}/`;

  return (
    <aside className="sidebar" aria-label="Studio navigation">
      <a
        className="sidebar__logo sidebar__logo--link"
        href={portalHome}
        aria-label="Back to Market Dwellings home"
      >
        Market Dwellings
        <small>Design Studio</small>
      </a>

      <button
        type="button"
        className="sidebar__cta"
        onClick={() => {
          // Phase B: opens the template gallery / Drafthouse. For v1 we
          // route to the full collection page on the live storefront
          // since template browsing isn't built yet.
          const shop = customer.shopify_shop_domain;
          window.location.href = `https://${shop}/collections/all`;
        }}
      >
        <Plus size={18} aria-hidden="true" className="plus" />
        <span>Start a new design</span>
      </button>

      <nav className="sidebar__nav">
        {primary.map((item) => (
          <NavButton key={item.view} item={item} active={view === item.view} onNavigate={onNavigate} />
        ))}

        <div className="sidebar__sep" />
        <div className="sidebar__group-label">My Business</div>
        {business.map((item) => (
          <NavButton key={item.view} item={item} active={view === item.view} onNavigate={onNavigate} />
        ))}

        <div className="sidebar__sep" />
        {footer.map((item) => (
          <NavButton key={item.view} item={item} active={view === item.view} onNavigate={onNavigate} />
        ))}
      </nav>

      <AccountMenu customer={customer} onNavigate={onNavigate} />
    </aside>
  );
}

function NavButton({ item, active, onNavigate }: { item: NavItem; active: boolean; onNavigate: (v: ViewName) => void }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      className={`side-item${active ? ' is-active' : ''}`}
      onClick={() => onNavigate(item.view)}
      aria-current={active ? 'page' : undefined}
    >
      <Icon className="ico" aria-hidden="true" strokeWidth={1.6} />
      <span className="label">{item.label}</span>
      {item.badge !== undefined && <span className="badge">{item.badge}</span>}
    </button>
  );
}
